import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { Banner } from '@ingenico/react-ui/banner';
import { ModalConfirmationDialog } from '@ingenico/react-ui/confirmation-dialog';
import { CoreModal } from '@ingenico/react-ui/core-modal';
import { DATAGRID_ROWS_PER_PAGE_OPTIONS, DataGridCore } from '@ingenico/react-ui/datagrid';
import { LinearProgress } from '@mui/material';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceConnectionStatusEnum } from '../../domain/models/deviceInfo.model';
import styles from './DeviceLogsModal.module.scss';
import { deviceLogsModalGridInitialState } from './DeviceLogsModal.util';
import { useDeviceLogsModalVM } from './useDeviceLogsModal.vm';

export interface DeviceLogsModalProps {
  readonly isOpen: boolean;
  readonly handleClose: () => void;
  readonly logicalDeviceId: string;
  readonly connectionStatus?: DeviceConnectionStatusEnum;
  readonly refetchIsInProgress: () => void;
}

export function DeviceLogsModal({
  isOpen,
  handleClose,
  logicalDeviceId,
  connectionStatus,
  refetchIsInProgress,
}: DeviceLogsModalProps): ReactElement {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE, ALL_LNGS_NAMESPACES.COMMON]);

  const { gridProps, columns, isFirstLoading, noRowsProps, isError, stopRecordingModal, cancelModal } =
    useDeviceLogsModalVM({
      logicalDeviceId,
      isOpen,
      connectionStatus,
      refetchIsInProgress,
    });

  return (
    <>
      <CoreModal
        cancelButtonText={t('button.close', { ns: ALL_LNGS_NAMESPACES.COMMON })}
        saveButtonText={t('button.confirm', { ns: ALL_LNGS_NAMESPACES.COMMON })}
        open={isOpen}
        onCloseModal={handleClose}
        title={t('deviceLogsModal.title', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
        subTitle={t('deviceLogsModal.subtitle', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
        className={styles.devicesLogsContainer}
        contentClassName={styles.contentContainer}
        cancelButtonProps={{ variant: 'text' }}
        hideSaveButton
      >
        <>
          {isError && !isFirstLoading ? (
            <Banner message={t('error.default', { ns: ALL_LNGS_NAMESPACES.COMMON })} />
          ) : undefined}
          {!isError && isFirstLoading ? <LinearProgress /> : undefined}
          {!isError && !isFirstLoading ? (
            <>
              <DataGridCore
                apiRef={gridProps.ref}
                columnBufferPx={8}
                columns={columns}
                disableMultipleColumnsSorting
                filterMode="server"
                initialState={deviceLogsModalGridInitialState}
                loading={gridProps.loading}
                onPaginationModelChange={gridProps.handlePaginationModelChange}
                onSortModelChange={gridProps.handleSortModelChange}
                noRowsProps={noRowsProps}
                paginationMode="server"
                rowCount={gridProps?.rowCount}
                rows={gridProps.rows}
                pageSizeOptions={DATAGRID_ROWS_PER_PAGE_OPTIONS}
                sortingMode="server"
                sortModel={gridProps.sortModel}
                getRowId={gridProps.getRowId}
              />
              <div className={styles.caption__container}>
                <span className={styles.caption__text}>
                  {t('deviceLogsModal.caption', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
                </span>
              </div>
            </>
          ) : undefined}
        </>
      </CoreModal>
      <ModalConfirmationDialog
        cancelLabel={t('button.back', { ns: ALL_LNGS_NAMESPACES.COMMON })}
        caption={t('stopRecordingModal.subtitle', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
        confirmLabel={t('stopRecordingModal.button.stop', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
        i18nNamespaces={[ALL_LNGS_NAMESPACES.COMMON]}
        isOpen={stopRecordingModal.isOpen}
        onClose={stopRecordingModal.handleClose}
        onConfirm={stopRecordingModal.handleConfirm}
        title={t('stopRecordingModal.title', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
        isConfirmLoading={stopRecordingModal.isLoading}
      />

      {cancelModal}
    </>
  );
}
